/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import apiClient from "@/utils/httpClient";
import { validateSession } from "@/services/auth.service";
import type { SessionData } from "@/interfaces/auth.interface.js";
import { ACTIVE_SELECTION_STORAGE_KEY } from "@/utils/activeSelectionStorage";

interface Organization {
  id: string;
  name: string;
}

interface View {
  id: string;
  name: string;
}

type Selection = { type: "organization" | "view"; id: string } | null;

function readSelection(): Selection {
  try {
    const raw = localStorage.getItem(ACTIVE_SELECTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.type === "organization" || parsed?.type === "view") return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeSelection(selection: Selection): void {
  try {
    if (selection) {
      localStorage.setItem(ACTIVE_SELECTION_STORAGE_KEY, JSON.stringify(selection));
    } else {
      localStorage.removeItem(ACTIVE_SELECTION_STORAGE_KEY);
    }
  } catch {
    // localStorage can throw in restrictive environments (e.g. Safari private browsing) — the
    // selection just won't persist across reloads, which is a fine degradation for this feature.
  }
}

interface OrganizationContextValue {
  organizations: Organization[];
  views: View[];
  activeOrganizationId: string | null;
  activeViewId: string | null;
  setActiveOrganizationId: (id: string) => void;
  setActiveView: (id: string) => void;
  addOrganization: (org: Organization) => void;
  addView: (view: View) => void;
  updateViewName: (id: string, name: string) => void;
  refreshOrganizations: () => Promise<SessionData | null>;
}

const OrganizationContext = createContext<OrganizationContextValue | null>(null);

// Runs once, synchronously, during the provider's first render — before any
// child effect can fire a request — so a persisted selection survives full
// page reloads/redirects (e.g. the QuickBooks OAuth round trip).
const primeHeadersFromStorage = (): Selection => {
  const selection = readSelection();
  if (selection?.type === "organization") apiClient.setOrganizationId(selection.id);
  else if (selection?.type === "view") apiClient.setViewId(selection.id);
  return selection;
};

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [views, setViews] = useState<View[]>([]);
  const [selection, setSelection] = useState<Selection>(primeHeadersFromStorage);

  const activeOrganizationId = selection?.type === "organization" ? selection.id : null;
  const activeViewId = selection?.type === "view" ? selection.id : null;

  const setActiveOrganizationId = useCallback((id: string) => {
    const next: Selection = { type: "organization", id };
    writeSelection(next);
    apiClient.setOrganizationId(id);
    setSelection(next);
  }, []);

  const setActiveView = useCallback((id: string) => {
    const next: Selection = { type: "view", id };
    writeSelection(next);
    apiClient.setViewId(id);
    setSelection(next);
  }, []);

  const addOrganization = useCallback((org: Organization) => {
    setOrganizations((prev) => (prev.some((o) => o.id === org.id) ? prev : [...prev, org]));
  }, []);

  const addView = useCallback((view: View) => {
    setViews((prev) => (prev.some((v) => v.id === view.id) ? prev : [...prev, view]));
  }, []);

  const updateViewName = useCallback((id: string, name: string) => {
    setViews((prev) => prev.map((v) => (v.id === id ? { ...v, name } : v)));
  }, []);

  const refreshOrganizations = useCallback(async (): Promise<SessionData | null> => {
    const session = await validateSession();
    if (!session) return null;

    if (session.organizations) {
      setOrganizations(session.organizations);
    }
    if (session.views) {
      setViews(session.views);
    }

    // Only fall back to the server's default active org when nothing is
    // currently selected client-side (e.g. first login) — an existing view
    // selection should survive a plain session refresh.
    setSelection((current) => {
      if (current) return current;
      const resolvedId = session.organization?.id;
      if (!resolvedId) return current;
      const next: Selection = { type: "organization", id: resolvedId };
      writeSelection(next);
      apiClient.setOrganizationId(resolvedId);
      return next;
    });

    return session;
  }, []);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        views,
        activeOrganizationId,
        activeViewId,
        setActiveOrganizationId,
        setActiveView,
        addOrganization,
        addView,
        updateViewName,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextValue => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};

export const clearActiveOrganization = (): void => {
  writeSelection(null);
  apiClient.setOrganizationId(null);
  apiClient.setViewId(null);
};
